import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TimeDepositStatusService from "service/amarBank/TimeDepositStatusService";
import {
  TimeDepositDto,
  TimeDepositDetailResp,
} from "domain/payload/response/bankamar/TimeDepositResp";
import TimeDepositDetailModal from "./modal/TimeDepositDetailModal";
import CustomDropdown from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

const TimeDepositStatus: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);

  const [data, setData] = useState<TimeDepositDto[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TimeDepositDetailResp | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(true);

  const timeDepositStatusService = TimeDepositStatusService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await timeDepositStatusService.getList(
        token,
        currentPage,
        pageSize,
        searchTerm || null
      );

      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data time deposit';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsSearchButtonDisabled(true);
    }
  };

  const handleDetail = async (id: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await timeDepositStatusService.getDetail(token, id);
      if (res?.data) {
        setSelectedItem(res);
        setShowModal(true);
      } else {
        window.alert('Data detail tidak ditemukan');
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail time deposit';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    setIsSearchButtonDisabled(false);
  }, [searchTerm]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Time Deposit Status</h5>
            <div>
              <p>Monitoring Transaction</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: "white" }}>
            {/* FILTER */}
            <div className="d-flex mb-3">
              <form className="d-flex me-2" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-small-compact h-100 p-4"
                  disabled={isSearchButtonDisabled}
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>
            </div>

            {/* TABLE */}
            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Partner Email</th>
                  <th>Account Number</th>
                  <th>Status</th>
                  <th>Timestamp</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td>{item.partnerCustomerId}</td>
                        <td>{item.partnerCustomerEmail}</td>
                        <td>{item.accountNumber}</td>
                        <td>{item.statusCode}</td>
                        <td>{item.timestamp}</td>
                        <td>
                          <button
                            className="btn btn-outline-info btn-small-compact border-0"
                            onClick={() => handleDetail(item.id)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    totalRecords
                  )} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>
                        {currentPage}
                      </span>
                    </li>

                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        style={{ border: 0 }}
                      >
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && selectedItem && (
        <TimeDepositDetailModal
          item={selectedItem}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default TimeDepositStatus;
